// ─────────────────────────────────────────────────────────────────────────────
// slim_pen_bridge.ts — Client to the local slim-pen-bridge helper
//
// The slim-pen-bridge is a separate Python service the user runs on
// their Windows machine to expose Slim Pen / Slim Pen 2 features the
// browser can't reach (the top click, haptics, raw HID). This module
// is the journal-side WebSocket client: auto-connects on app start,
// reports status to the canvas, forwards bridge events into the app's
// pipelines (hookup added as the bridge grows), and accepts outbound
// commands from the canvas (e.g. haptic taps).
//
// If the bridge isn't running, this module silently retries in the
// background — the journal works exactly as before, just without the
// extras the bridge would offer.
// ─────────────────────────────────────────────────────────────────────────────

export type BridgeStatus = 'disconnected' | 'connecting' | 'connected';

export interface BridgePenInfo {
  model?: string;
  battery?: number | null;
  haptic?: boolean;
}

export interface BridgeCapabilities {
  tailClick?: boolean;
  rawTilt?: boolean;
  haptic?: boolean;
  battery?: boolean;
}

/** Server-pushed events that other modules (canvas / bindings) may
 *  subscribe to. Forward shape — fields will fill in as bridge stages
 *  ship. Currently the only stable kinds are 'hello' (connection
 *  handshake) and 'heartbeat' (liveness). */
export interface BridgePenEvent {
  kind: string;
  ts?: number;
  source?: string;
  value?: unknown;
}

const DEFAULT_URL = 'ws://localhost:7779/pen';
const RECONNECT_BACKOFF_MS = [1000, 2000, 5000, 10000];   // ramp + cap

export class SlimPenBridge {
  private _ws: WebSocket | null = null;
  private _url: string;
  private _status: BridgeStatus = 'disconnected';
  private _penInfo: BridgePenInfo | null = null;
  private _capabilities: BridgeCapabilities = {};
  private _reconnectAttempt = 0;
  private _reconnectTimer = 0;
  private _stopped = false;

  /** Status changes — disconnected / connecting / connected. The
   *  canvas binds this to update the Slim Pen settings panel. */
  onStatusChange: ((status: BridgeStatus, info: BridgePenInfo | null, caps: BridgeCapabilities) => void) | null = null;
  /** Per-event push from the bridge (tail-click etc., as stages land). */
  onPenEvent: ((event: BridgePenEvent) => void) | null = null;

  constructor(url: string = DEFAULT_URL) {
    this._url = url;
  }

  /** Begin auto-connecting. Idempotent. */
  start(): void {
    if (!this._stopped && this._ws) return;
    this._stopped = false;
    this._connect();
  }

  /** Stop and stay disconnected. */
  stop(): void {
    this._stopped = true;
    clearTimeout(this._reconnectTimer);
    if (this._ws) {
      try { this._ws.close(); } catch { /* ignore */ }
      this._ws = null;
    }
    this._setStatus('disconnected');
  }

  /** Send a JSON message to the bridge. No-op when not connected. */
  send(message: { kind: string; [key: string]: unknown }): void {
    const ws = this._ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try { ws.send(JSON.stringify(message)); } catch { /* swallow */ }
  }

  /** Convenience: ask the Slim Pen 2 haptic motor to fire. No-op if
   *  the bridge doesn't yet support haptics. */
  haptic(pattern: 'tap' | 'long' | 'click' = 'tap'): void {
    this.send({ kind: 'haptic', pattern });
  }

  // ── Read-only state ────────────────────────────────────────────────────

  get status(): BridgeStatus { return this._status; }
  get penInfo(): BridgePenInfo | null { return this._penInfo; }
  get capabilities(): BridgeCapabilities { return this._capabilities; }
  get connected(): boolean { return this._status === 'connected'; }

  // ── Internals ──────────────────────────────────────────────────────────

  private _connect(): void {
    if (this._stopped) return;
    this._setStatus('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(this._url);
    } catch {
      this._scheduleReconnect();
      return;
    }
    this._ws = ws;
    ws.addEventListener('open', () => {
      this._reconnectAttempt = 0;
      // 'connected' is set by the 'hello' message, not by socket open
      // alone, so we know we're talking to a real bridge before
      // surfacing the UI.
    });
    ws.addEventListener('message', (e) => this._onMessage(e.data));
    ws.addEventListener('close', () => {
      this._ws = null;
      this._penInfo = null;
      this._capabilities = {};
      this._setStatus('disconnected');
      this._scheduleReconnect();
    });
    // Errors fire 'close' afterwards; nothing extra to do here.
    ws.addEventListener('error', () => { /* close will follow */ });
  }

  private _onMessage(data: string | ArrayBuffer | Blob): void {
    if (typeof data !== 'string') return;
    let msg: { kind?: string; [k: string]: unknown };
    try { msg = JSON.parse(data); } catch { return; }
    if (!msg || typeof msg !== 'object') return;

    switch (msg.kind) {
      case 'hello': {
        this._penInfo = (msg.pen as BridgePenInfo) ?? {};
        this._capabilities = (msg.capabilities as BridgeCapabilities) ?? {};
        this._setStatus('connected');
        break;
      }
      case 'pen-info': {
        // Bridge pushed an updated snapshot — battery changed, pen
        // appeared / disappeared, etc. Refresh local mirror and
        // notify the canvas so the settings panel redraws.
        this._penInfo = (msg.pen as BridgePenInfo) ?? this._penInfo;
        this._capabilities = (msg.capabilities as BridgeCapabilities) ?? this._capabilities;
        this.onStatusChange?.(this._status, this._penInfo, this._capabilities);
        break;
      }
      case 'heartbeat':
        // Liveness only — no app state to update.
        break;
      case 'pen-event':
        this.onPenEvent?.(msg as BridgePenEvent);
        break;
      case 'pong':
        // Latency probe response — ignored for now.
        break;
      default:
        // Forward-compat: unknown kinds are surfaced to the listener
        // so future bridge versions don't need a journal release.
        if (msg.kind) this.onPenEvent?.(msg as BridgePenEvent);
    }
  }

  private _setStatus(status: BridgeStatus): void {
    if (this._status === status) return;
    this._status = status;
    this.onStatusChange?.(status, this._penInfo, this._capabilities);
  }

  private _scheduleReconnect(): void {
    if (this._stopped) return;
    clearTimeout(this._reconnectTimer);
    const idx = Math.min(this._reconnectAttempt, RECONNECT_BACKOFF_MS.length - 1);
    const delay = RECONNECT_BACKOFF_MS[idx];
    this._reconnectAttempt++;
    this._reconnectTimer = window.setTimeout(() => this._connect(), delay);
  }
}
